import React, { useState, useRef, useLayoutEffect } from "react";
import { ChevronRight, Link as LinkIcon, Users } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { hasPermission } from "@/lib/permissions";

// Right-click / long-press context menu for an appointment block. Positioned
// absolutely against the viewport at the pointer's (x, y). Smart Menu
// Positioning (Iter-281):
//   • measures the rendered menu height with useLayoutEffect and picks
//     UPWARD vs DOWNWARD direction so late-day / bottom-of-screen tiles
//     never spill under the browser chrome.
//   • preferred direction is UP when the pointer sits in the bottom 40%
//     of the viewport OR the appointment's local start time (Asia/Makassar
//     — the calendar's TZ) is 15:00 or later.
//   • clamps left/right/top/bottom against a small padding so every edge
//     of the menu is inside the viewport.
//   • same clamp runs for Link / Group / Take-Payment submenus so they
//     flip up when the parent menu opens near the bottom edge.
// Actions bubble back to the parent via the handler props; the parent is
// responsible for closing the menu after each action fires.
export default function ContextMenu({
  x, y, appt,
  onCheckInGuest,
  onTakePayment, onTakePaymentGroup, onTakePaymentSolo,
  onTakeDeposit, onCancel, onMarkNoShow, onRevertNoShow, onLock, onClipboard,
  onEdit, onPrint, onLink, onLinkTo, onUnlink, onSeeProfile, onAIBriefing,
  onMarkPickedUp,
  // Group booking
  currentGroup = null,               // group this appt belongs to (with .name, .primary_appointment_id, .members)
  activeGroups = [],                 // active groups on the calendar day the appt is in
  packageName = null,                // resolved package name if this tile is a package sibling
  onCreateGroup, onAddToGroup, onSetPrimaryPayer, onRemoveFromGroup, onDissolveGroup,
  // Candidates for the Link submenu — the parent pre-filters appts on the
  // same customer + same date + not this appt + not already same link group.
  // Shape: { id, service_name, start_iso, staff_name }
  linkCandidates = [],
}) {
  // Booking Calendar permissions — Cancel is gated on `.delete`, Edit on
  // `.edit`. When the actor lacks the tick, the corresponding item is
  // hidden entirely so operators never see an inert button.
  const { staff } = useAuth();
  const canDeleteAppt = hasPermission(staff, "location.booking_calendar.delete");
  const canEditAppt   = hasPermission(staff, "location.booking_calendar.edit");
  // Compact context menu — 60% of the previous 240px width with a
  // matching drop in font size and vertical padding so operators on
  // small tablets see every option without scrolling. Submenus mirror
  // the same trim.
  const w = 144;
  const [linkOpen, setLinkOpen] = useState(false);
  const [groupOpen, setGroupOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  // Viewport-aware submenu direction. Measured after the parent menu is
  // positioned so operators near the right edge of a small tablet don't
  // get a submenu that opens off-screen.
  const linkAnchorRef = useRef(null);
  const groupAnchorRef = useRef(null);
  const payAnchorRef = useRef(null);
  const [linkLeft, setLinkLeft] = useState(false);
  const [groupLeft, setGroupLeft] = useState(false);
  const [payLeft, setPayLeft] = useState(false);
  // Iter-281 · Vertical flip flags for submenus. When the parent menu
  // opens near the bottom of the viewport the submenu's default
  // `top: 0` anchor still extends downward and can spill under the
  // browser chrome. `*Up` flips the submenu to `bottom: 0` so it
  // opens above the anchor row instead.
  const [linkUp, setLinkUp] = useState(false);
  const [groupUp, setGroupUp] = useState(false);
  const [payUp, setPayUp] = useState(false);
  const submenuW = 156;

  // Iter-281 · Smart Menu Positioning. Measure the actual menu size after
  // the first paint so the flip / clamp math uses real geometry rather
  // than a hard-coded estimate. The menu is rendered `visibility: hidden`
  // on the first tick, then made visible once positioned.
  const menuRef = useRef(null);
  const [pos, setPos] = useState({ left: x, top: y, ready: false });

  // Preferred direction: UPWARD when the pointer sits in the bottom 40%
  // of the viewport OR the appointment's local start (Asia/Makassar — the
  // calendar's TZ) is at or after 15:00.
  const apptHour = (() => {
    try {
      const startIso = appt && (appt.start_iso || appt.start);
      if (!startIso) return 0;
      const s = new Date(startIso).toLocaleString("en-GB", {
        hour: "2-digit", hour12: false, timeZone: "Asia/Makassar",
      });
      return parseInt(s, 10) || 0;
    } catch { return 0; }
  })();

  useLayoutEffect(() => {
    const el = menuRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const menuH = rect.height || 260;
    const menuW = rect.width || w;
    const vh = (typeof window !== "undefined" ? window.innerHeight : 900);
    const vw = (typeof window !== "undefined" ? window.innerWidth : 1024);
    const pad = 8;

    const inBottomZone = y > vh * 0.6;
    const isLateAppt = apptHour >= 15;
    const preferUp = inBottomZone || isLateAppt;
    const roomAbove = y - pad;
    const roomBelow = vh - y - pad;

    let top;
    if (preferUp && roomAbove >= menuH) {
      top = y - menuH;
    } else if (!preferUp && roomBelow >= menuH) {
      top = y;
    } else if (roomAbove >= menuH) {
      top = y - menuH;
    } else if (roomBelow >= menuH) {
      top = y;
    } else {
      // Menu taller than either side — pin to top with padding and let
      // the browser scroll the parent container if needed.
      top = pad;
    }
    // Final clamp so no edge ever bleeds off-screen.
    top = Math.max(pad, Math.min(top, vh - menuH - pad));

    let left = Math.min(x, vw - menuW - pad);
    left = Math.max(pad, left);

    setPos({ left, top, ready: true });
  }, [x, y, apptHour]);

  useLayoutEffect(() => {
    const submenuH = 220; // conservative estimate for Link / Group / Pay submenus
    const compute = (open, ref, setterX, setterY) => {
      if (!open) return;
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const roomRight = window.innerWidth - rect.right;
      setterX(roomRight < submenuW + 8);
      // Flip submenu upward when its default downward reach would spill
      // off the viewport bottom AND there is more room above.
      const roomBelow = window.innerHeight - rect.top;
      const roomAbove = rect.bottom;
      setterY(roomBelow < submenuH + 8 && roomAbove > roomBelow);
    };
    compute(linkOpen, linkAnchorRef, setLinkLeft, setLinkUp);
    compute(groupOpen, groupAnchorRef, setGroupLeft, setGroupUp);
    compute(payOpen, payAnchorRef, setPayLeft, setPayUp);
  }, [linkOpen, groupOpen, payOpen, pos.top]);

  const isNoShow = appt.status === "no_show";
  // Iter-254 · Transportation-only tiles carry `is_transportation=true`
  // and represent a driver pick-up, not a client visit. Every other
  // action (check-in, payment, deposit, cancel-with-reason, group,
  // link, edit, no-show, see profile, AI briefing) is intentionally
  // suppressed — the driver only needs Delete + Print.
  const isTransportation = !!appt.is_transportation;
  // Print Invoice gate (Jun 2026): the option only exists when this
  // appointment actually has a SETTLED invoice behind it. The calendar
  // feed enriches every tile with its linked order
  // (`order_id` / `order_status` / `order_payment_status` — see
  // `routes/appointments.py` list endpoint), so "closed or paid" is
  // decided on the real invoice, never on the tile's own status. No
  // order, an open/pending one, or an unpaid bill → the item is not
  // rendered at all.
  const hasSettledInvoice = !!appt.order_id && (
    ["completed", "closed"].includes(String(appt.order_status || "").toLowerCase())
    || String(appt.order_payment_status || "").toLowerCase() === "paid"
  );
  // Check-In Gate (Feb 2026): "Take Payment / Check-Out" is disabled
  // until the guest is physically checked in — a QR / front-desk
  // check-in stamps `checked_in_at` and turns the tile teal. The
  // deposit shortcut (below) stays ALWAYS clickable so the front desk
  // can secure a deposit before the guest arrives. The gate is
  // driven by `appt.checked_in_at` directly, so a live-sync push
  // (`customer_updated` / `appointment_updated` from the check-in
  // wizard) unlocks the button the instant the tile refetches.
  const isCheckedIn = !!appt.checked_in_at;
  const payDisabledTitle = "Client must be Checked-In before final payment can be taken.";
  const inGroup = !!currentGroup;
  const isPrimary = inGroup && currentGroup.primary_appointment_id === appt.id;
  const primaryName = inGroup ? (currentGroup.primary_customer_name || "Primary") : "";
  const inPackage = !!appt.package_id;
  // Same-day active groups that this appointment isn't already a member of.
  const joinableGroups = activeGroups.filter((g) =>
    g.id !== (currentGroup && currentGroup.id) &&
    !(g.members || []).some((m) => m.appointment_id === appt.id)
  );

  const timeOf = (iso) => new Date(iso).toLocaleTimeString("en-GB", {
    hour: "2-digit", minute: "2-digit", timeZone: "Asia/Makassar", hour12: false,
  });

  return (
    <div
      ref={menuRef}
      className="fixed z-50 bg-white rounded-md shadow-lg border border-border py-0 text-[10px] leading-none"
      style={{ left: pos.left, top: pos.top, width: w, visibility: pos.ready ? "visible" : "hidden" }}
      onContextMenu={(e) => e.preventDefault()}
      onClick={(e) => e.stopPropagation()}
      data-testid="appt-context-menu"
      data-menu-direction={pos.ready ? (pos.top < y ? "up" : "down") : "pending"}
    >
      {/* Iter-254 · Transportation short menu. Only Delete + Print
          — no check-in, payment, cancel reason, group, link, or
          edit surfaces here. The Delete option hard-removes the
          tile (no cancellation-reason gate). */}
      {isTransportation ? (
        <>
          {appt.status !== "picked_up" && (
            <button
              className="w-full text-left px-2 py-[2.2px] flex items-center min-h-[20px] hover:bg-secondary/60 font-medium"
              onClick={onMarkPickedUp}
              data-testid="ctx-transport-picked-up"
            >
              Mark as Picked Up
            </button>
          )}
          <button
            className="w-full text-left px-2 py-[2.2px] flex items-center min-h-[20px] hover:bg-red-50 text-red-700 font-medium"
            onClick={onCancel}
            data-testid="ctx-transport-delete"
          >
            Delete
          </button>
          <button
            className="w-full text-left px-2 py-[2.2px] flex items-center min-h-[20px] hover:bg-secondary/60"
            onClick={onPrint}
            data-testid="ctx-transport-print"
          >
            Print
          </button>
        </>
      ) : (
      <>
      {/* No-Show short menu — when the appointment is settled as a
          no-show the ONLY relevant next actions are "Revert" (guest
          arrived late) or "View Client Profile" (audit / follow-up).
          Every other action — Check-In, Take Payment, Take Deposit,
          Group Booking, Edit — is intentionally suppressed to keep
          the operator on a single, deliberate path. */}
      {isNoShow ? (
        <>
          <button
            className="w-full text-left px-2 py-[2.2px] flex items-center min-h-[20px] hover:bg-secondary/60"
            onClick={onRevertNoShow}
            data-testid="ctx-revert-noshow"
          >
            Revert No-Show
          </button>
          <button
            className="w-full text-left px-2 py-[2.2px] flex items-center min-h-[20px] hover:bg-secondary/60 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            onClick={onSeeProfile}
            disabled={!appt.customer_id}
            data-testid="ctx-see-profile"
          >
            View Client Profile
          </button>
        </>
      ) : (
      <>
      {/* Front-Desk Check-In — top of the menu, only for appointments the
          guest hasn't already checked in on. "Booked" and "Open" are the
          two statuses where the guest is expected to arrive; once the
          appointment is closed / cancelled / no-show / checked-out there
          is nothing left to send. */}
      {onCheckInGuest && (appt.status === "booked" || appt.status === "open") && appt.customer_id && !appt.checked_in_at && (
        <>
          <button
            className="w-full text-left px-2 py-[2.2px] flex items-center min-h-[20px] hover:bg-emerald-50 text-emerald-800 font-medium"
            onClick={onCheckInGuest}
            data-testid="ctx-checkin-guest"
          >
            Check-In Guest
          </button>
          <div className="my-0 border-t border-border" />
        </>
      )}
      {/* Take Payment — flat when solo, submenu when in a group. Gated
          on `checked_in_at`: an unchecked-in guest never gets to
          Check-Out. The container div still receives hover so the
          tooltip surfaces on iPad tap. */}
      {inGroup ? (
        <div
          ref={payAnchorRef}
          className="relative"
          onMouseEnter={() => { if (isCheckedIn) setPayOpen(true); }}
          onMouseLeave={() => setPayOpen(false)}
          title={isCheckedIn ? undefined : payDisabledTitle}
          data-testid="ctx-take-payment-wrap"
          data-checked-in={isCheckedIn ? "true" : "false"}
        >
          <button
            className={
              "w-full text-left px-2 py-[2.2px] flex items-center justify-between min-h-[20px] " +
              (isCheckedIn ? "hover:bg-secondary/60" : "opacity-40 cursor-not-allowed")
            }
            disabled={!isCheckedIn}
            title={isCheckedIn ? undefined : payDisabledTitle}
            data-testid="ctx-take-payment"
          >
            <span>Take Payment/Check-Out</span>
            <ChevronRight className="w-3 h-3 opacity-60" />
          </button>
          {isCheckedIn && payOpen && (
            <div
              className={
                "absolute bg-white rounded-md shadow-lg border border-border py-0 " +
                (payUp ? "bottom-0 " : "top-0 ") +
                (payLeft ? "right-full -mr-px " : "left-full -ml-px ")
              }
              style={{ width: submenuW }}
              data-testid="ctx-take-payment-submenu"
              data-submenu-direction={payUp ? "up" : "down"}
            >
              <button
                className="w-full text-left px-2 py-[2.2px] flex items-center min-h-[20px] hover:bg-secondary/60"
                onClick={onTakePaymentGroup}
                data-testid="ctx-take-payment-group"
              >
                <div className="font-medium">Entire group</div>
                <div className="text-[10px] text-muted-foreground">Consolidated bill under {primaryName}</div>
              </button>
              <button
                className="w-full text-left px-2 py-[2.2px] flex items-center min-h-[20px] hover:bg-secondary/60"
                onClick={onTakePaymentSolo}
                data-testid="ctx-take-payment-solo"
              >
                <div className="font-medium">This client only</div>
                <div className="text-[10px] text-muted-foreground">{appt.customer_name || "Walk-in"}&apos;s bill</div>
              </button>
            </div>
          )}
          {!isCheckedIn && (
            <div
              className="px-2 pb-1 text-[9px] leading-tight text-muted-foreground"
              data-testid="ctx-take-payment-hint"
            >
              {payDisabledTitle}
            </div>
          )}
        </div>
      ) : (
        <div
          title={isCheckedIn ? undefined : payDisabledTitle}
          data-testid="ctx-take-payment-wrap"
          data-checked-in={isCheckedIn ? "true" : "false"}
        >
          <button
            className={
              "w-full text-left px-2 py-[2.2px] flex items-center min-h-[20px] " +
              (isCheckedIn ? "hover:bg-secondary/60" : "opacity-40 cursor-not-allowed")
            }
            onClick={isCheckedIn ? onTakePayment : undefined}
            disabled={!isCheckedIn}
            title={isCheckedIn ? undefined : payDisabledTitle}
            data-testid="ctx-take-payment"
          >Take Payment/Check-Out</button>
          {!isCheckedIn && (
            <div
              className="px-2 pb-1 text-[9px] leading-tight text-muted-foreground"
              data-testid="ctx-take-payment-hint"
            >
              {payDisabledTitle}
            </div>
          )}
        </div>
      )}

      {/* Take Deposit — ALWAYS clickable. Front desks routinely secure a
          deposit before a guest walks in (esp. for high-value packages),
          so no check-in gate here. */}
      <button className="w-full text-left px-2 py-[2.2px] flex items-center min-h-[20px] hover:bg-secondary/60" onClick={onTakeDeposit} data-testid="ctx-take-deposit">Take Deposit</button>

      {/* Mark as No-Show — one-click path to the No-Show reason picker.
          Only surfaced for appointments still eligible for the flip
          (booked / open, guest attached) and requires the same delete
          permission as Cancel. Deposits stay on the linked order (the
          order remains Open) so the manager can decide later whether
          to refund or keep as a fee — see CancelAppointmentDialog. */}
      {!isNoShow && (appt.status === "booked" || appt.status === "open") && canDeleteAppt && onMarkNoShow && (
        <button
          className="w-full text-left px-2 py-[2.2px] flex items-center min-h-[20px] hover:bg-secondary/60"
          onClick={onMarkNoShow}
          data-testid="ctx-mark-noshow"
        >
          Mark as No-Show
        </button>
      )}

      {isNoShow ? null : (
        canDeleteAppt && (
          <button className="w-full text-left px-2 py-[2.2px] flex items-center min-h-[20px] hover:bg-secondary/60" onClick={onCancel} data-testid="ctx-cancel">Cancel Appointment</button>
        )
      )}
      <button className="w-full text-left px-2 py-[2.2px] flex items-center min-h-[20px] hover:bg-secondary/60" onClick={onLock} data-testid="ctx-lock">{appt.locked_to_staff ? "Unlock Staff" : "Lock to Staff"}</button>

      <div className="my-0 border-t border-border" />

      <button className="w-full text-left px-2 py-[2.2px] flex items-center min-h-[20px] hover:bg-secondary/60 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent" onClick={onSeeProfile} disabled={!appt.customer_id} data-testid="ctx-see-profile">See Profile</button>
      <button className="w-full text-left px-2 py-[2.2px] flex items-center min-h-[20px] hover:bg-secondary/60 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent" onClick={onAIBriefing} disabled={!appt.customer_id} data-testid="ctx-ai-briefing">AI Briefing</button>
      <button className="w-full text-left px-2 py-[2.2px] flex items-center min-h-[20px] hover:bg-secondary/60" onClick={onClipboard} data-testid="ctx-clipboard">Move to Clipboard</button>
      {canEditAppt && (
        <button className="w-full text-left px-2 py-[2.2px] flex items-center min-h-[20px] hover:bg-secondary/60" onClick={onEdit} data-testid="ctx-edit">Edit</button>
      )}
      {/* Print Invoice — Jun 2026: offered ONLY when this appointment has a
          settled invoice behind it (closed / paid). Unpaid, order-less and
          open/pending appointments hide it completely. Clicking it opens the
          same document the Order/Bill page prints. */}
      {hasSettledInvoice && (
        <button className="w-full text-left px-2 py-[2.2px] flex items-center min-h-[20px] hover:bg-secondary/60" onClick={onPrint} data-testid="ctx-print">Print Invoice</button>
      )}

      {/* Group submenu — behaves differently depending on whether the
          appointment is already in a group. */}
      <div
        ref={groupAnchorRef}
        className="relative"
        onMouseEnter={() => setGroupOpen(true)}
        onMouseLeave={() => setGroupOpen(false)}
      >
        <button
          className="w-full text-left px-2 py-[2.2px] hover:bg-secondary/60 flex items-center justify-between min-h-[20px]"
          data-testid="ctx-group"
        >
          <span className="flex items-center gap-1.5"><Users className="w-3 h-3" />{inGroup ? `Group: ${currentGroup.name || "Unnamed"}` : (inPackage ? `Package: ${packageName || "Group Booking"}` : "Group Booking")}</span>
          <ChevronRight className="w-3 h-3 opacity-60" />
        </button>
        {groupOpen && (
          <div
            className={
              "absolute bg-white rounded-md shadow-lg border border-border py-0 " +
              (groupUp ? "bottom-0 " : "top-0 ") +
              (groupLeft ? "right-full -mr-px " : "left-full -ml-px ")
            }
            style={{ width: submenuW, maxHeight: "60vh", overflowY: "auto" }}
            data-testid="ctx-group-submenu"
            data-submenu-direction={groupUp ? "up" : "down"}
          >
            {inGroup ? (
              <>
                <div className="px-2 py-0.5 text-[10px] text-muted-foreground border-b border-border">
                  Primary Payer: <span className="font-medium text-foreground">{primaryName}</span>
                  <div className="mt-0.5">{(currentGroup.members || []).length} member(s)</div>
                </div>
                {!isPrimary && (
                  <button
                    className="w-full text-left px-2 py-[2.2px] flex items-center min-h-[20px] hover:bg-secondary/60 text-[10px]"
                    onClick={onSetPrimaryPayer}
                    data-testid="ctx-group-set-primary"
                  >Set as Primary Payer</button>
                )}
                {isPrimary && (
                  <div className="px-2 py-0.5 text-[10px] italic text-muted-foreground">This client is the Primary Payer.</div>
                )}
                <button
                  className="w-full text-left px-2 py-[2.2px] flex items-center min-h-[20px] hover:bg-secondary/60 text-[10px]"
                  onClick={onRemoveFromGroup}
                  data-testid="ctx-group-remove"
                >{isPrimary ? "Remove Primary Payer (splits group)" : "Remove from Group"}</button>
                <div className="my-0 border-t border-border" />
                <button
                  className="w-full text-left px-2 py-[2.2px] flex items-center min-h-[20px] hover:bg-secondary/60 text-[10px] text-red-600"
                  onClick={onDissolveGroup}
                  data-testid="ctx-group-dissolve"
                >Dissolve group…</button>
              </>
            ) : (
              <>
                {inPackage && (
                  <div className="px-2 py-0.5 text-[10px] text-muted-foreground border-b border-border" data-testid="ctx-group-package-hint">
                    Part of package{packageName ? `: ${packageName}` : ""}. Create a group to consolidate billing across guests.
                  </div>
                )}
                <button
                  className="w-full text-left px-2 py-[2.2px] flex items-center min-h-[20px] hover:bg-secondary/60 text-[10px] font-medium"
                  onClick={onCreateGroup}
                  data-testid="ctx-group-create"
                >+ Create new group…</button>
                {joinableGroups.length > 0 && (
                  <>
                    <div className="my-0 border-t border-border" />
                    <div className="px-2 py-[2.2px] text-[9px] uppercase tracking-wider text-muted-foreground">Add to existing</div>
                    {joinableGroups.map((g) => (
                      <button
                        key={g.id}
                        className="w-full text-left px-2 py-[2.2px] flex items-center min-h-[20px] hover:bg-secondary/60 text-[10px]"
                        onClick={() => onAddToGroup && onAddToGroup(g.id)}
                        data-testid={`ctx-group-add-${g.id}`}
                      >
                        <div className="font-medium truncate">{g.name}</div>
                        <div className="text-[10px] text-muted-foreground">
                          Primary: {g.primary_customer_name || "—"} · {(g.members || []).length} member(s)
                        </div>
                      </button>
                    ))}
                  </>
                )}
                {joinableGroups.length === 0 && (
                  <div className="px-2 py-0.5 text-[10px] italic text-muted-foreground">
                    No other groups on this day yet.
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <div className="my-0 border-t border-border" />

      {/* Link submenu — hover to slide the candidate list out. */}
      {appt.link_group_id ? (
        <button className="w-full text-left px-2 py-[2.2px] flex items-center min-h-[20px] hover:bg-secondary/60" onClick={onUnlink} data-testid="ctx-link">Unlink</button>
      ) : (
        <div
          ref={linkAnchorRef}
          className="relative"
          onMouseEnter={() => setLinkOpen(true)}
          onMouseLeave={() => setLinkOpen(false)}
        >
          <button
            className="w-full text-left px-2 py-[2.2px] hover:bg-secondary/60 flex items-center justify-between min-h-[20px]"
            data-testid="ctx-link"
          >
            <span className="flex items-center gap-1.5"><LinkIcon className="w-3 h-3" />Link</span>
            <ChevronRight className="w-3 h-3 opacity-60" />
          </button>
          {linkOpen && (
            <div
              className={
                "absolute bg-white rounded-md shadow-lg border border-border py-0 " +
                (linkUp ? "bottom-0 " : "top-0 ") +
                (linkLeft ? "right-full -mr-px " : "left-full -ml-px ")
              }
              style={{ width: submenuW, maxHeight: "60vh", overflowY: "auto" }}
              data-testid="ctx-link-submenu"
              data-submenu-direction={linkUp ? "up" : "down"}
            >
              {linkCandidates.length === 0 ? (
                <div className="px-2 py-[3px] text-[10px] text-muted-foreground italic" data-testid="ctx-link-empty">
                  No other bookings on this day for {appt.customer_name || "this guest"}.
                </div>
              ) : (
                linkCandidates.map((c) => (
                  <button
                    key={c.id}
                    className="w-full text-left px-2 py-[2.2px] flex items-center min-h-[20px] hover:bg-secondary/60 text-[10px]"
                    onClick={() => onLinkTo && onLinkTo(c.id)}
                    data-testid={`ctx-link-candidate-${c.id}`}
                  >
                    <div className="font-medium truncate">{c.service_name}</div>
                    <div className="text-[10px] text-muted-foreground">
                      {timeOf(c.start_iso)}{c.staff_name ? ` · ${c.staff_name}` : ""}
                    </div>
                  </button>
                ))
              )}
              <div className="my-0 border-t border-border" />
              <button
                className="w-full text-left px-2 py-[2.2px] flex items-center min-h-[20px] hover:bg-secondary/60 text-[10px] text-muted-foreground"
                onClick={onLink}
                data-testid="ctx-link-manual"
              >Enter appointment ID…</button>
            </div>
          )}
        </div>
      )}
      </>
      )}
      </>
      )}
    </div>
  );
}
